import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { 
  getCardForCollaboration, 
  updateCardCollaboration, 
  getCollaborationFeatures 
} from "../utils/api";
import Button from "./ui/Button";
import { UserPlus, Sparkles, Crown, Save, Eye, Plus, Trash2, ChevronLeft, ChevronRight, Type, MessageSquare, Image, Play, Check, Loader2, Send } from "lucide-react";
import PreviewModal from "./PreviewModal";
import GreetingCardCanvas from "./GreetingCard/GreetingCardCanvas";
import VideoCanvas from "./GreetingCard/VideoCanvas";
import { CARD_W, CARD_H } from "../utils/geometry";

// Helper to parse width/height from card_size string like "800x600"
const parseWidth = (s) => {
  const m = /^(\d+)x\d+$/i.exec(s || "");
  return m ? +m[1] : null;
};

const parseHeight = (s) => {
  const m = /^\d+x(\d+)$/i.exec(s || "");
  return m ? +m[1] : null;
};

// Helper to detect video files by extension
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext));
};

export default function CollaborationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Card data
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Track actual image dimensions for proper aspect ratio
  const [imageDimensions, setImageDimensions] = useState({ width: CARD_W, height: CARD_H });
  // Track if dimensions were restored from saved card_size to prevent image load from overwriting
  const dimensionsRestoredRef = useRef(false);
  
  // Session ID to track which entries this collaborator added (so they can only delete their own)
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // Cover/front text styling (loaded from saved card layout)
  const [frontTextLayout, setFrontTextLayout] = useState({
    x: CARD_W * 0.1,
    y: CARD_H * 0.2,
    width: CARD_W * 0.8,
    height: CARD_H * 0.6,
    fontSize: 48,
    fontFamily: "Lilita One",
    fill: "#000000",
    align: "center",
    stroke: "",
    strokeWidth: 0,
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
    animStyle: "none",  // bounce, wiggle, none
    transform: "none",
    lineHeight: 1.2,
    letterSpacing: 1,
  });
  
  // Cover border styling (loaded from saved card layout)
  const [coverBorderColor, setCoverBorderColor] = useState("");
  const [coverBorderWidth, setCoverBorderWidth] = useState(0);
  const [coverBorderStyle, setCoverBorderStyle] = useState("solid");
  
  // Cover overlay styling (loaded from saved card layout)
  const [coverOverlay, setCoverOverlay] = useState("none");
  const [coverOverlayColor, setCoverOverlayColor] = useState("#1F2937");
  
  // Inside text entries - array of independent text boxes for collaboration
  // Each entry: { id, text, x, y, width, fontSize, fontFamily, fill, author }
  const [insideTextEntries, setInsideTextEntries] = useState([]);
  
  // Legacy support: inside pages for backward compatibility with old cards
  const [insidePages, setInsidePages] = useState([""]);
  const [currentInsidePage, setCurrentInsidePage] = useState(0);
  const [insideLayout, setInsideLayout] = useState({
    x: CARD_W * 0.1,
    y: CARD_H * 0.1,
    width: CARD_W * 0.8,
    height: CARD_H * 0.8,
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
  });
  const [insideFont, setInsideFont] = useState("'Inter', sans-serif");
  const [insideFontSize, setInsideFontSize] = useState(24);
  const [insideFill, setInsideFill] = useState("#000000");
  
  // User message state
  const [userMessage, setUserMessage] = useState("");
  
  // Helper to generate unique ID for new text entries
  const generateEntryId = () => `entry_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Signatures
  const [signatures, setSignatures] = useState([]);
  const [newSignature, setNewSignature] = useState("");
  
  // UI states
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState("");
  const [saveError, setSaveError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [features, setFeatures] = useState(null);
  const [tab, setTab] = useState("inside"); // Start with inside tab like EditingPage
  
  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle, saving, saved, error
  const autoSaveTimerRef = useRef(null);
  const lastSavedDataRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    const loadCard = async () => {
      try {
        const cardParams = token ? { token } : {};
        const [cardResponse, featuresResponse] = await Promise.all([
          getCardForCollaboration(id, cardParams),
          getCollaborationFeatures(id)
        ]);
        
        const cardData = cardResponse.data;
        setCard(cardData);
        
        // Parse inside text into pages
        const insideText = cardData.inside_text || "";
        const pages = insideText ? insideText.split("\n").map(t => t.trim()).filter(Boolean) : [""];
        setInsidePages(pages.length > 0 ? pages : [""]);
        
        // Load inside layout if exists
        if (cardData.inside_layout) {
          setInsideLayout(prev => ({
            ...prev,
            x: typeof cardData.inside_layout.x === 'number' ? cardData.inside_layout.x : prev.x,
            y: typeof cardData.inside_layout.y === 'number' ? cardData.inside_layout.y : prev.y,
            width: typeof cardData.inside_layout.width === 'number' ? cardData.inside_layout.width : prev.width,
            height: typeof cardData.inside_layout.height === 'number' ? cardData.inside_layout.height : prev.height,
            rotation: typeof cardData.inside_layout.rotation === 'number' ? cardData.inside_layout.rotation : prev.rotation,
            scaleX: typeof cardData.inside_layout.scaleX === 'number' ? cardData.inside_layout.scaleX : prev.scaleX,
            scaleY: typeof cardData.inside_layout.scaleY === 'number' ? cardData.inside_layout.scaleY : prev.scaleY,
          }));
        }
        
        // Load inside style if exists
        if (cardData.inside_style) {
          setInsideFont(cardData.inside_style.fontFamily || "'Inter', sans-serif");
          setInsideFontSize(cardData.inside_style.fontSize || 24);
          setInsideFill(cardData.inside_style.fill || "#000000");
        }
        
        // Load inside text entries (collaborator messages only)
        // Note: inside_text is the owner's text - NOT converted to entries
        // inside_text_entries contains ONLY collaborator-added messages
        if (cardData.inside_text_entries && Array.isArray(cardData.inside_text_entries)) {
          setInsideTextEntries(cardData.inside_text_entries);
          console.log("[CollaborationPage] Loaded inside_text_entries:", cardData.inside_text_entries);
        }
        // Owner's inside_text stays in insidePages for display (read-only for collaborators)
        
        // Load card dimensions from saved card_size (like EditingPage does)
        if (cardData.card_size) {
          const savedWidth = parseWidth(cardData.card_size);
          const savedHeight = parseHeight(cardData.card_size);
          if (savedWidth && savedHeight) {
            setImageDimensions({ width: savedWidth, height: savedHeight });
            // Mark that dimensions were restored - prevents image load from overwriting
            dimensionsRestoredRef.current = true;
            console.log("[CollaborationPage] Restored card dimensions from card_size:", { savedWidth, savedHeight });
          }
        }
        
        // Load front/cover text layout and styling from saved card layout
        if (cardData.layout) {
          const savedLayout = cardData.layout;
          setFrontTextLayout({
            x: typeof savedLayout.x === 'number' ? savedLayout.x : CARD_W * 0.1,
            y: typeof savedLayout.y === 'number' ? savedLayout.y : CARD_H * 0.2,
            width: typeof savedLayout.width === 'number' ? savedLayout.width : CARD_W * 0.8,
            height: typeof savedLayout.height === 'number' ? savedLayout.height : CARD_H * 0.6,
            fontSize: savedLayout.fontSize || 48,
            fontFamily: savedLayout.fontFamily || "Lilita One",
            fill: savedLayout.fill || "#000000",
            align: savedLayout.align || "center",
            stroke: savedLayout.stroke || "",
            strokeWidth: savedLayout.strokeWidth || 0,
            rotation: savedLayout.rotation || 0,
            scaleX: savedLayout.scaleX || 1,
            scaleY: savedLayout.scaleY || 1,
            animStyle: savedLayout.animStyle || "none",
            transform: savedLayout.transform || "none",
            lineHeight: savedLayout.lineHeight || 1.2,
            letterSpacing: savedLayout.letterSpacing || 1,
          });
          
          // Load cover border settings
          setCoverBorderColor(savedLayout.borderColor || "");
          setCoverBorderWidth(savedLayout.borderWidth || 0);
          setCoverBorderStyle(savedLayout.borderStyle || "solid");
          
          // Load cover overlay settings
          setCoverOverlay(savedLayout.overlay || "none");
          setCoverOverlayColor(savedLayout.overlayColor || "#1F2937");
        }
        
        // Load signatures
        setSignatures(cardData.signatures || []);
        setFeatures(featuresResponse.data);
        
      } catch (err) {
        console.error("Failed to load card for collaboration:", err);
        setError(
          err.response?.data?.detail || 
          "Could not load card for collaboration. Make sure you have the correct link."
        );
      } finally {
        setLoading(false);
      }
    };

    loadCard();
  }, [id, token]);

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO-SAVE EFFECT - Automatically saves changes after 2 seconds of inactivity
  // ═══════════════════════════════════════════════════════════════════════════
  const performAutoSave = useCallback(async () => {
    if (!card || !id) return;
    
    const currentData = JSON.stringify({
      inside_text_entries: insideTextEntries,
      signatures: signatures,
    });
    
    // Skip if no changes since last save
    if (currentData === lastSavedDataRef.current) {
      return;
    }
    
    setAutoSaveStatus("saving");
    
    try {
      const updateData = {
        inside_text_entries: insideTextEntries,
        signatures: signatures.map((s) => String(s || "").trim()).filter(Boolean),
      };

      const params = token ? { token } : {};
      await updateCardCollaboration(id, updateData, params);
      
      lastSavedDataRef.current = currentData;
      setAutoSaveStatus("saved");
      
      // Reset status after 3 seconds
      setTimeout(() => setAutoSaveStatus("idle"), 3000);
      
    } catch (err) {
      console.error("[Auto-save] Failed:", err);
      setAutoSaveStatus("error");
      // Reset status after 5 seconds
      setTimeout(() => setAutoSaveStatus("idle"), 5000);
    }
  }, [card, id, token, insideTextEntries, signatures]);

  useEffect(() => {
    // Skip auto-save during initial load
    if (isInitialLoadRef.current) {
      // Mark initial load complete after a short delay
      const timeout = setTimeout(() => {
        isInitialLoadRef.current = false;
        // Store initial data as "saved" state
        lastSavedDataRef.current = JSON.stringify({
          inside_text_entries: insideTextEntries,
          signatures: signatures,
        });
      }, 1000);
      return () => clearTimeout(timeout);
    }
    
    // Clear any existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set a new timer for auto-save (2 second debounce)
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, 2000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [insideTextEntries, signatures, performAutoSave]);

  // Track actual image dimensions for proper canvas/container sizing
  useEffect(() => {
    if (!card?.image_url) return;
    
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Skip if dimensions were already restored from saved card_size
      // This prevents the image load from overwriting the saved dimensions
      if (dimensionsRestoredRef.current) {
        console.log('[CollaborationPage] Skipping dimension update - using restored card_size dimensions');
        return;
      }
      console.log('[CollaborationPage] Image loaded with dimensions:', img.width, 'x', img.height);
      setImageDimensions({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      console.warn('[CollaborationPage] Failed to load image for dimension tracking');
    };
    img.src = card.image_url;
  }, [card?.image_url]);

  // Update inside page text (legacy support)
  const updateInsidePage = (text) => {
    const newPages = [...insidePages];
    newPages[currentInsidePage] = text;
    setInsidePages(newPages);
  };

  // Add new page (legacy support)
  const handleAddPage = () => {
    setInsidePages([...insidePages, ""]);
    setCurrentInsidePage(insidePages.length);
  };

  // Remove page (legacy support)
  const handleRemovePage = (index) => {
    if (insidePages.length <= 1) return; // Keep at least one page
    const newPages = insidePages.filter((_, i) => i !== index);
    setInsidePages(newPages);
    setCurrentInsidePage(prev => Math.min(prev, newPages.length - 1));
  };

  // === NEW: Text Entry Handlers for Collaboration ===
  
  // Add a new text entry with signature (when collaborator adds their message)
  const handleAddTextEntry = (messageText, signatureName = "") => {
    if (!messageText || !messageText.trim()) return;
    
    // Calculate position for new entry - stagger entries to avoid overlap
    const entryCount = insideTextEntries.length;
    const baseY = imageDimensions.height * 0.1;
    // Account for signature when calculating vertical spacing
    const entryHeight = insideFontSize * 3 + (signatureName ? insideFontSize * 1.5 : 0);
    const yOffset = entryCount * entryHeight;
    const newY = Math.min(baseY + yOffset, imageDimensions.height * 0.6); // Don't go too far down
    
    const newEntry = {
      id: generateEntryId(),
      text: messageText.trim(),
      signature: signatureName.trim() || "", // Signature shown under this entry
      x: imageDimensions.width * 0.1,
      y: newY,
      width: imageDimensions.width * 0.8,
      fontSize: insideFontSize,
      fontFamily: insideFont,
      fill: insideFill,
      align: "left",
      author: signatureName.trim() || "Anonymous", // Track who added it
      addedBySession: sessionId, // Track which session added this entry
      createdAt: new Date().toISOString(),
    };
    
    setInsideTextEntries(prev => [...prev, newEntry]);
    console.log("[CollaborationPage] Added new text entry with signature:", newEntry);
  };
  
  // Update a specific text entry (when user drags or edits it)
  const handleTextEntryChange = (entryId, updatedProps) => {
    setInsideTextEntries(prev => 
      prev.map(entry => 
        entry.id === entryId 
          ? { ...entry, ...updatedProps }
          : entry
      )
    );
  };
  
  // Remove a text entry
  const handleRemoveTextEntry = (entryId) => {
    setInsideTextEntries(prev => prev.filter(entry => entry.id !== entryId));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveSuccess("");

    try {
      // Only update collaborator entries - don't overwrite owner's inside_text
      const updateData = {
        // Save text entries as structured data for collaboration
        // Note: We do NOT update inside_text - that's the owner's text
        inside_text_entries: insideTextEntries,
        signatures: signatures.map((s) => String(s || "").trim()).filter(Boolean),
      };

      console.log("[CollaborationPage] Saving card data:", {
        cardId: id,
        updateData,
        entriesCount: insideTextEntries.length,
      });

      const params = token ? { token } : {};
      const response = await updateCardCollaboration(id, updateData, params);
      console.log("[CollaborationPage] Save response:", response);
      setSaveSuccess("✅ Card updated successfully!");
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(""), 3000);
      
    } catch (err) {
      console.error("Failed to save card:", err);
      setSaveError(
        err.response?.data?.detail || 
        "Failed to save changes. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddSignature = () => {
    if (newSignature.trim()) {
      setSignatures([...signatures, newSignature.trim()]);
      setNewSignature("");
    }
  };

  const handleRemoveSignature = (index) => {
    setSignatures(signatures.filter((_, i) => i !== index));
  };

  function handleInsideTextChange(props) {
    // Handle both object props and string text updates
    if (typeof props === 'string') {
      // Direct text update from canvas (e.g., double-click edit)
      if (props !== insidePages[currentInsidePage]) {
        updateInsidePage(props);
      }
    } else if (props && typeof props === 'object') {
      // Handle layout changes (dragging, positioning) - only update if actually changed
      const hasLayoutChange = (
        (props.x !== undefined && props.x !== insideLayout.x) ||
        (props.y !== undefined && props.y !== insideLayout.y) ||
        (props.width !== undefined && props.width !== insideLayout.width) ||
        (props.height !== undefined && props.height !== insideLayout.height) ||
        (props.rotation !== undefined && props.rotation !== insideLayout.rotation) ||
        (props.scaleX !== undefined && props.scaleX !== insideLayout.scaleX) ||
        (props.scaleY !== undefined && props.scaleY !== insideLayout.scaleY)
      );

      if (hasLayoutChange) {
        setInsideLayout(prev => ({
          ...prev,
          x: props.x !== undefined ? props.x : prev.x,
          y: props.y !== undefined ? props.y : prev.y,
          width: props.width !== undefined ? props.width : prev.width,
          height: props.height !== undefined ? props.height : prev.height,
          rotation: props.rotation !== undefined ? props.rotation : prev.rotation,
          scaleX: props.scaleX !== undefined ? props.scaleX : prev.scaleX,
          scaleY: props.scaleY !== undefined ? props.scaleY : prev.scaleY,
          align: "left", // Keep inside text left-aligned and freely draggable
        }));
      }

      // Handle text content changes from canvas - only if different
      if (props.text !== undefined && props.text !== insidePages[currentInsidePage]) {
        updateInsidePage(props.text);
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading card...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Collaboration Not Available
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/")} variant="secondary">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  // Detect if this is a video card (video might be in image_url field)
  const isVideo = card?.media_type === "video" || !!card?.video_url || isVideoUrl(card?.image_url);
  const actualVideoUrl = card?.video_url || (isVideoUrl(card?.image_url) ? card?.image_url : null);
  const actualImageUrl = isVideo ? null : card?.image_url;

  // Prepare card state for preview - matching EditingPage structure exactly
  const cardState = {
    imageUrl: actualImageUrl,
    videoUrl: actualVideoUrl,
    mediaType: isVideo ? "video" : "image",
    cardWidth: imageDimensions.width,
    cardHeight: imageDimensions.height,
    text: {
      // Pass layout directly like EditingPage does - no scaling needed
      ...frontTextLayout,
      text: card?.front_text || "",
      align: frontTextLayout.align || "center",
      animStyle: frontTextLayout.animStyle || "none",
      draggable: false, // Front text not editable in collaboration
    },
    setText: () => {}, // No-op for front text
    inside: insidePages[currentInsidePage] || "",
    setInside: (newText) => {
      // Handle preview modal page updates properly
      // PreviewModal expects this to be the joined text from all pages
      if (typeof newText === 'string') {
        // If it's a string, it's likely from PreviewModal's join operation
        // Split it back into pages and update our state
        const pages = newText.split('\n').map(p => p.trim()).filter(p => p.length > 0);
        if (pages.length === 0) pages.push(''); // Ensure at least one page
        setInsidePages(pages);
        // Keep current page within bounds
        if (currentInsidePage >= pages.length) {
          setCurrentInsidePage(Math.max(0, pages.length - 1));
        }
      } else {
        // Direct page update
        updateInsidePage(newText);
      }
    },
    insideFont: insideFont,
    insideFontSize: insideFontSize,
    insideFill: insideFill,
    insideLayout: insideLayout, // Include inside text positioning for preview
    insideBorderColor: card?.inside_style?.borderColor || "",
    insideBorderWidth: card?.inside_style?.borderWidth || 0,
    insideBorderStyle: card?.inside_style?.borderStyle || "solid",
    // Cover border/overlay styling for preview
    coverBorderColor: coverBorderColor,
    coverBorderWidth: coverBorderWidth,
    coverBorderStyle: coverBorderStyle,
    coverOverlay: coverOverlay,
    coverOverlayColor: coverOverlayColor,
    signatures: signatures,
    insidePages: insidePages,
    // Multiple text entries for collaboration preview
    insideTextEntries: insideTextEntries,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <UserPlus className="h-6 w-6" />
                Collaboration Mode
              </h1>
              <p className="text-gray-600">
                Edit the inside of this {card?.category || "greeting"} card for{" "}
                <span className="font-medium">{card?.recipient_name}</span>
              </p>
              {token && (
                <p className="text-xs text-green-600 mt-1">
                  🔒 Secure collaboration link activated
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Auto-save status indicator */}
              <div className="flex items-center gap-2 text-sm">
                {autoSaveStatus === "saving" && (
                  <span className="flex items-center gap-1.5 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </span>
                )}
                {autoSaveStatus === "saved" && (
                  <span className="flex items-center gap-1.5 text-green-600">
                    <Check className="h-4 w-4" />
                    Saved
                  </span>
                )}
                {autoSaveStatus === "error" && (
                  <span className="flex items-center gap-1.5 text-red-500">
                    <span className="text-xs">⚠️</span>
                    Save failed
                  </span>
                )}
              </div>
              
              <Button 
                onClick={() => setShowPreview(true)}
                variant="secondary"
                className="flex items-center gap-1"
              >
                <Eye className="h-4 w-4" />
                Preview Card
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Auto-save error message (only show persistent errors) */}
        {autoSaveStatus === "error" && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <span className="text-red-500 text-xl">⚠️</span>
            <div>
              <p className="text-red-800 font-medium">Failed to save changes</p>
              <p className="text-red-600 text-sm">Please check your connection and try refreshing the page.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Card Display */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm p-2">
              <div className="flex space-x-1">
                <button
                  onClick={() => setTab("cover")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
                    tab === "cover"
                      ? "bg-purple-100 text-purple-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <Image className="h-4 w-4" />
                  Card Cover
                </button>
                <button
                  onClick={() => setTab("inside")}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
                    tab === "inside"
                      ? "bg-purple-100 text-purple-700"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                >
                  <MessageSquare className="h-4 w-4" />
                  Inside Message
                </button>
              </div>
            </div>

            {/* Card Cover Display */}
            {tab === "cover" && (
              <div className="space-y-4">
                {/* Header - outside the gray container like EditingPage structure */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Card Cover</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {isVideo ? "This is a video greeting card" : "This is the cover everyone will see"}
                    </span>
                    {isVideo ? (
                      <Play className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Image className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Card Cover Canvas - matching EditingPage container structure exactly */}
                <div className="bg-gray-100 rounded-lg p-4 sm:p-6 lg:p-8">
                  <div className="flex justify-center items-center w-full">
                    <div
                      className="relative rounded-lg overflow-hidden inline-flex"
                      style={{
                        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.25), 0 4px 10px rgba(0, 0, 0, 0.15)",
                        backgroundColor: "transparent",
                        maxWidth: "100%",
                        maxHeight: "70vh",
                      }}
                    >
                      {isVideo && actualVideoUrl ? (
                        /* Video greeting card cover with text and frame overlay */
                        (() => {
                          // Calculate scaled dimensions to fit within container while preserving aspect ratio
                          const maxWidth = Math.min(window.innerWidth * 0.9, 800);
                          const maxHeight = window.innerHeight * 0.7;
                          const aspectRatio = imageDimensions.width / imageDimensions.height;
                          
                          let scaledWidth = imageDimensions.width;
                          let scaledHeight = imageDimensions.height;
                          
                          if (scaledWidth > maxWidth) {
                            scaledWidth = maxWidth;
                            scaledHeight = scaledWidth / aspectRatio;
                          }
                          if (scaledHeight > maxHeight) {
                            scaledHeight = maxHeight;
                            scaledWidth = scaledHeight * aspectRatio;
                          }
                          
                          const scaleFactor = scaledWidth / imageDimensions.width;
                          
                          return (
                        <div className="relative" style={{ width: scaledWidth, height: scaledHeight }}>
                          <VideoCanvas
                            videoSrc={actualVideoUrl}
                            cardWidth={scaledWidth}
                            cardHeight={scaledHeight}
                            autoPlay={true}
                            loop={true}
                            muted={true}
                            showControls={false}
                            isPreview={false}
                            className="w-full h-full"
                          />
                          
                          {/* Frame/Border overlay for video - positioned to wrap exactly */}
                          {coverBorderWidth > 0 && coverBorderColor && (
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                border: `${coverBorderWidth}px ${coverBorderStyle} ${coverBorderColor}`,
                                boxSizing: 'border-box',
                                zIndex: 10,
                              }}
                            />
                          )}
                          
                          {/* Text overlay for video - scaled with container */}
                          {card?.front_text && (
                            <div
                              className="absolute pointer-events-none"
                              style={{
                                left: (frontTextLayout.x || imageDimensions.width * 0.1) * scaleFactor,
                                top: (frontTextLayout.y || imageDimensions.height * 0.1) * scaleFactor,
                                width: (frontTextLayout.width || imageDimensions.width * 0.8) * scaleFactor,
                                fontFamily: frontTextLayout.fontFamily || 'Lilita One',
                                fontSize: (frontTextLayout.fontSize || 48) * scaleFactor,
                                fontWeight: frontTextLayout.fontWeight || 700,
                                color: frontTextLayout.fill || '#000000',
                                textAlign: frontTextLayout.align || 'center',
                                lineHeight: frontTextLayout.lineHeight || 1.2,
                                letterSpacing: (frontTextLayout.letterSpacing || 1) * scaleFactor,
                                WebkitTextStroke: frontTextLayout.stroke && frontTextLayout.strokeWidth 
                                  ? `${frontTextLayout.strokeWidth * scaleFactor}px ${frontTextLayout.stroke}` 
                                  : 'none',
                                transform: frontTextLayout.rotation ? `rotate(${frontTextLayout.rotation}deg)` : 'none',
                                whiteSpace: 'pre-wrap',
                                wordWrap: 'break-word',
                                // CSS animation for video card text
                                animation: frontTextLayout.animStyle === 'bounce' 
                                  ? 'bounceSubtle 1s ease-in-out infinite' 
                                  : frontTextLayout.animStyle === 'wiggle'
                                  ? 'wiggle 0.5s ease-in-out infinite'
                                  : 'none',
                              }}
                            >
                              {card.front_text}
                            </div>
                          )}
                          
                          {/* CSS animations for video card text */}
                          <style>{`
                            @keyframes bounceSubtle {
                              0%, 100% { transform: scale(1) ${frontTextLayout.rotation ? `rotate(${frontTextLayout.rotation}deg)` : ''}; }
                              50% { transform: scale(1.03) ${frontTextLayout.rotation ? `rotate(${frontTextLayout.rotation}deg)` : ''}; }
                            }
                            @keyframes wiggle {
                              0%, 100% { transform: rotate(${(frontTextLayout.rotation || 0) - 2}deg); }
                              50% { transform: rotate(${(frontTextLayout.rotation || 0) + 2}deg); }
                            }
                          `}</style>
                          
                          {/* Decorative overlay for video */}
                          {coverOverlay && coverOverlay !== 'none' && (
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                backgroundImage: coverOverlay === 'snow-splatter' 
                                  ? 'radial-gradient(circle, white 2px, transparent 2px)'
                                  : coverOverlay === 'stars'
                                  ? 'radial-gradient(circle, gold 1px, transparent 1px)'
                                  : 'none',
                                backgroundSize: coverOverlay === 'snow-splatter' ? '30px 30px' : '40px 40px',
                                opacity: 0.6,
                              }}
                            />
                          )}
                        </div>
                          );
                        })()
                      ) : (
                        /* Image greeting card cover */
                        <GreetingCardCanvas
                          side="front"
                          bgSrc={card?.image_url}
                          textProps={{
                            // Pass layout directly like EditingPage does - no scaling needed
                            ...frontTextLayout,
                            text: card?.front_text || "",
                            align: frontTextLayout.align || "center",
                            draggable: false, // Cover is not editable in collaboration
                          }}
                          setText={() => {}} // No-op for front text
                          cardWidth={imageDimensions.width}
                          cardHeight={imageDimensions.height}
                          coverBorderColor={coverBorderColor}
                          coverBorderWidth={coverBorderWidth}
                          coverBorderStyle={coverBorderStyle}
                          coverOverlay={coverOverlay}
                          coverOverlayColor={coverOverlayColor}
                          fitToImage={true}
                        />
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <strong>This is the card everyone is collaborating on!</strong> Switch to the "Inside Message" tab to add your message and see what others have written.
                  </p>
                </div>
              </div>
            )}

            {/* Inside Message Display */}
            {tab === "inside" && (
              <div className="space-y-4">
                {/* Header - outside the gray container like EditingPage structure */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Inside Message</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Drag text to position it</span>
                    <Type className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                
                {/* Canvas Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Type className="h-4 w-4 text-blue-600" />
                    <h3 className="font-semibold text-blue-700">Canvas Editing</h3>
                  </div>
                  <div className="text-sm space-y-1 text-blue-700">
                    <p>• <strong>Drag</strong> text on the canvas to position it</p>
                    <p>• <strong>Double-click</strong> text on the canvas to edit it</p>
                    <p>• Use the "Your Message" panel or edit directly on canvas</p>
                    <p>• Multiple people can edit this card together!</p>
                  </div>
                </div>
                
                {/* Card Canvas - matching EditingPage container structure exactly */}
                <div className="bg-gray-100 rounded-lg p-4 sm:p-6 lg:p-8">
                  <div className="flex justify-center items-center w-full">
                    <div
                      className="relative rounded-lg overflow-hidden inline-flex"
                      style={{
                        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.25), 0 4px 10px rgba(0, 0, 0, 0.15)",
                        backgroundColor: "#ffffff", // White background for inside
                        maxWidth: "100%",
                        maxHeight: "70vh",
                      }}
                    >
                      <GreetingCardCanvas
                        side="inside"
                        textProps={{
                          ...insideLayout,
                          // Owner's text - read-only for collaborators
                          text: card?.inside_text || "",
                          fontSize: insideFontSize,
                          fontFamily: insideFont,
                          fill: insideFill,
                          draggable: false, // Owner's text not movable by collaborators
                          align: "left",
                          wrap: "word",
                        }}
                        setText={() => {}} // Owner's text not editable by collaborators
                        cardWidth={imageDimensions.width}
                        cardHeight={imageDimensions.height}
                        insideBorderColor={card?.inside_style?.borderColor || ""}
                        insideBorderWidth={card?.inside_style?.borderWidth || 0}
                        insideBorderStyle={card?.inside_style?.borderStyle || "solid"}
                        // Collaborator text entries - only draggable if added by current session
                        insideTextEntries={insideTextEntries.map(entry => ({
                          ...entry,
                          draggable: entry.addedBySession === sessionId, // Only drag your own entries
                        }))}
                        onTextEntryChange={(entryId, updatedProps) => {
                          // Only allow changes to entries added by this session
                          const entry = insideTextEntries.find(e => e.id === entryId);
                          if (entry && entry.addedBySession === sessionId) {
                            handleTextEntryChange(entryId, updatedProps);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Owner's Message (read-only for collaborators) */}
                {card?.inside_text && card.inside_text.trim() && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-blue-700 mb-2">
                    Card Owner's Message:
                  </div>
                  <p 
                    className="text-gray-800"
                    style={{
                      fontFamily: insideFont,
                      fontSize: Math.min(16, insideFontSize),
                      color: insideFill,
                    }}
                  >
                    {card.inside_text}
                  </p>
                  <p className="text-xs text-blue-600 mt-2 italic">
                    This message was written by the card owner
                  </p>
                </div>
              )}

                {/* Collaborator Text Entries List */}
                {insideTextEntries.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-600 mb-3">
                    Collaborator Messages ({insideTextEntries.length}):
                  </div>
                  <div className="space-y-2">
                    {insideTextEntries.map((entry, i) => (
                      <div
                        key={entry.id}
                        className="flex items-start justify-between bg-white p-3 rounded-lg border border-gray-200"
                      >
                        <div className="flex-1 mr-2">
                          <p 
                            className="text-gray-800 text-sm"
                            style={{
                              fontFamily: entry.fontFamily || insideFont,
                              color: entry.fill || insideFill,
                            }}
                          >
                            {entry.text.length > 100 ? entry.text.slice(0, 100) + "..." : entry.text}
                          </p>
                          {entry.signature && (
                            <p 
                              className="text-sm mt-1 italic"
                              style={{
                                fontFamily: entry.fontFamily || insideFont,
                                color: entry.fill || insideFill,
                                opacity: 0.8,
                              }}
                            >
                              — {entry.signature}
                            </p>
                          )}
                        </div>
                        {/* Only show delete button for entries added by this session */}
                        {entry.addedBySession === sessionId && (
                          <button
                            onClick={() => handleRemoveTextEntry(entry.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Remove your message"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    💡 Drag any message on the canvas to reposition it independently
                  </p>
                </div>
              )}

                {/* Signatures display below card */}
                {signatures.length > 0 && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm font-medium text-gray-600 mb-3">Signatures (will appear at bottom of card):</div>
                  <div className="space-y-2">
                    {signatures.map((sig, i) => (
                      <div
                        key={i}
                        className="text-right text-gray-800"
                        style={{
                          fontFamily: insideFont || "'Inter', sans-serif",
                          fontSize: Math.max(14, insideFontSize * 0.7),
                          fontStyle: 'italic',
                        }}
                      >
                        — {sig}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2 italic">
                    Preview the card to see how signatures will appear on the final card
                  </div>
                </div>
              )}

                {/* Page Navigation */}
                {insidePages.length > 1 && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Button
                    onClick={() => setCurrentInsidePage(prev => Math.max(0, prev - 1))}
                    disabled={currentInsidePage === 0}
                    variant="secondary"
                    size="sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {currentInsidePage + 1} of {insidePages.length}
                  </span>
                  <Button
                    onClick={() => setCurrentInsidePage(prev => Math.min(insidePages.length - 1, prev + 1))}
                    disabled={currentInsidePage === insidePages.length - 1}
                    variant="secondary"
                    size="sm"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Right Column - Controls */}
          <div className="space-y-6">
            {/* User Message Input */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Your Message
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your message
                  </label>
                  <textarea
                    value={userMessage}
                    onChange={(e) => setUserMessage(e.target.value)}
                    placeholder="Write your message here... (e.g., 'Happy Birthday! Hope you have an amazing day!')"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    rows={4}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your name (signature)
                  </label>
                  <input
                    type="text"
                    value={newSignature}
                    onChange={(e) => setNewSignature(e.target.value)}
                    placeholder="e.g., Sarah, Uncle Bob, The Team"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Your name will appear under your message
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (userMessage.trim()) {
                      // Create a new independent text box with signature
                      handleAddTextEntry(userMessage, newSignature);
                      setUserMessage(""); // Clear the input
                      setNewSignature(""); // Clear signature
                    }
                  }}
                  disabled={!userMessage.trim()}
                  className={`
                    w-full py-3 px-4 rounded-xl font-semibold text-white
                    flex items-center justify-center gap-2
                    transition-all duration-200 transform
                    ${userMessage.trim() 
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] cursor-pointer' 
                      : 'bg-gray-300 cursor-not-allowed'
                    }
                  `}
                >
                  <Send className="h-5 w-5" />
                  Add Message to Card
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  ✨ Your message will appear on the card and auto-save
                </p>
              </div>
            </div>

            {/* Text Styling */}
            {tab === "inside" && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Text Styling</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                  <select
                    value={insideFont}
                    onChange={(e) => setInsideFont(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="'Inter', sans-serif">Inter (Sans Serif)</option>
                    <option value="'Georgia', serif">Georgia (Serif)</option>
                    <option value="'Cedarville Cursive', cursive">Cedarville Cursive</option>
                    <option value="'Patrick Hand', cursive">Patrick Hand</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Font Size: {insideFontSize}px
                  </label>
                  <input
                    type="range"
                    min="12"
                    max="60"
                    value={insideFontSize}
                    onChange={(e) => setInsideFontSize(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                  <div className="flex items-center gap-2">
                    {["#000000", "#EF4444", "#10B981", "#3B82F6", "#FBBF24"].map((color) => (
                      <button
                        key={color}
                        onClick={() => setInsideFill(color)}
                        style={{ backgroundColor: color }}
                        className={`h-8 w-8 rounded-full border-2 transition ${
                          insideFill === color
                            ? "border-purple-600 ring-2 ring-purple-100"
                            : "border-gray-200"
                        }`}
                      />
                    ))}
                    <input
                      type="color"
                      value={insideFill}
                      onChange={(e) => setInsideFill(e.target.value)}
                      className="h-8 w-8 rounded border border-gray-200 cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Page Management */}
            {tab === "inside" && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Pages</h3>
              
              <div className="space-y-3">
                {insidePages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <button
                      onClick={() => setCurrentInsidePage(index)}
                      className={`flex-1 text-left px-2 py-1 rounded text-sm ${
                        currentInsidePage === index
                          ? "bg-purple-100 text-purple-700 font-medium"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      Page {index + 1}: {page.slice(0, 20)}{page.length > 20 ? "..." : ""}
                    </button>
                    {insidePages.length > 1 && (
                      <button
                        onClick={() => handleRemovePage(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
                
                <Button
                  onClick={handleAddPage}
                  variant="secondary"
                  className="w-full flex items-center justify-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add Page
                </Button>
              </div>
            </div>
            )}

            {/* Signatures */}
            {tab === "inside" && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Signatures</h3>
              
              {/* Existing signatures */}
              {signatures.length > 0 && (
                <div className="mb-4 space-y-2">
                  {signatures.map((sig, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm">{sig}</span>
                      <button
                        onClick={() => handleRemoveSignature(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add new signature */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newSignature}
                  onChange={(e) => setNewSignature(e.target.value)}
                  placeholder="Your name"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSignature()}
                />
                <Button onClick={handleAddSignature} variant="secondary">
                  Add
                </Button>
              </div>
            </div>
            )}

            {/* Premium Features Prompt */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <Crown className="h-6 w-6 text-purple-600 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Want More Editing Power?
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Create a free account to unlock AI-powered features and advanced editing tools.
                  </p>
                  
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => navigate("/register", { 
                        state: { 
                          fromCollaboration: true,
                          cardId: id,
                          message: "Create a free account to unlock AI-powered card editing!" 
                        } 
                      })}
                      className="flex items-center gap-1 text-sm"
                    >
                      <Sparkles className="h-4 w-4" />
                      Create Free Account
                    </Button>
                    <Button
                      onClick={() => navigate("/login", { 
                        state: { 
                          fromCollaboration: true,
                          cardId: id 
                        } 
                      })}
                      variant="secondary"
                      className="text-sm"
                    >
                      Sign In
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <PreviewModal
          cardState={cardState}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
} 